import {
  init,
  captureException,
  captureMessage,
  addBreadcrumb,
  withScope,
  setUser,
} from '@sentry/vue';

import { sentryConfig } from './config';
import { sentryLib } from './lib';
import type { ErrorWithCause } from './lib';

type SentryConfig = Parameters<typeof init>[0];
const isProductionRuntime = import.meta.env.VITE_RUNTIME_ENV === 'production';
const isDevelopmentRuntime =
  import.meta.env.VITE_RUNTIME_ENV &&
  import.meta.env.VITE_RUNTIME_ENV === 'development';

function initRealSentry(config: SentryConfig) {
  const sampleRate = isProductionRuntime ? 0.6 : 1.0; // Track all errors in staging and only 60% in production mode
  init({
    debug: !isProductionRuntime,
    dist: import.meta.env.VITE_PLUGIN_SENTRY_CONFIG?.dist || '',
    release: import.meta.env.VITE_PLUGIN_SENTRY_CONFIG?.release || '',
    denyUrls: ['localhost', '127.0.0.1'], // For development sentry logging testing comment out this line
    sendClientReports: false,
    autoSessionTracking: true,
    enableTracing: true,
    sampleRate: 1, // Temporary capture all errors/messages (TODO: capture only specific messages fully)
    tracesSampleRate: sampleRate,
    ...config,
  });
}

function initSentry(config: SentryConfig) {
  if (isDevelopmentRuntime) {
    sentryLib.debugMsg('Development Sentry is initiated');
  } else {
    initRealSentry(config);
  }
}

function getSentryInstance() {
  if (isDevelopmentRuntime) {
    return {
      addBreadcrumb,
      withScope,
      setUser(userData: Record<string, unknown>) {
        sentryLib.debugMsg('User is set', userData);
      },
      captureException<T extends ErrorWithCause>(error: T) {
        sentryLib.debugError('Captured error:', error);

        if (sentryLib.isErrorWithCause(error)) {
          sentryLib.debugError('Cause:', error.cause);
        }
      },
      captureMessage: sentryLib.debugMsg,
      getReadableError: sentryLib.getReadableError,
      createCustomErrorText: sentryLib.createCustomErrorText,
      breadcrumbCategories: sentryConfig.BREADCRUMB_CATEGORIES,
      level: sentryConfig.LEVEL,
      errorPrefix: sentryConfig.ERROR_PREFIXES,
    };
  }

  return {
    captureException,
    captureMessage,
    addBreadcrumb,
    withScope,
    setUser,
    getReadableError: sentryLib.getReadableError,
    createCustomErrorText: sentryLib.createCustomErrorText,
    breadcrumbCategories: sentryConfig.BREADCRUMB_CATEGORIES,
    level: sentryConfig.LEVEL,
    errorPrefix: sentryConfig.ERROR_PREFIXES,
  };
}

export const sentryBaseModel = {
  initSentry,
  getSentryInstance,
};

export type SentryInstance = ReturnType<typeof getSentryInstance>;
